import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClbTag } from '@celebration/react/ClbTag/ClbTag';
import { useTranslation } from 'react-i18next';
import ITableHead from '../../../../common/components/table/interfaces/ITableHead';
import Table from '../../../../common/components/table';
import IUsersTable from '../../interfaces/IUsersTable';
import UsersManagementService from '../../services/users-management.service';
import UsersConstants from '../../constants';
import IPagination from '../../../../common/interfaces/IPagination';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import RequestsTableRowButtons from './components/requests-table-row-buttons/requests-table-row-buttons';
import IUserRequestsFilterModel from '../../interfaces/IUserRequestsFilterModel';
import IUsersRequestsResponse from '../../interfaces/IUsersRequestsResponse';
import IUserRequestsDataTable from '../../interfaces/IUserRequestsDataTable';
import { SULAHUB_USERS_BY_USER_TYPE } from '../../../../common/constants/roles';
import UserRequestStatus from './enums/user-request-status';
import ReproveUserModal from './components/reprove-user-modal/reprove-user-modal';
import ApproveUserModal from './components/approve-user-modal/approve-user-modal';
import authContext from '../../../../app/contexts/auth/auth.context';

export interface IApproveUser {
    userId: string;
    userType?: number;
    userTypeLabel?: string;
}

function RequestsTable({ searchTerm }: Readonly<Partial<IUsersTable>>) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const { isThirdUser } = authContext.useAuth();

    const [usersDataTable, setUsersDataTable] = useState<
        IPagination<IUserRequestsDataTable>
    >(UsersConstants.DEFAULT_DATA_USER_REQUEST);

    const filters = useRef<IUserRequestsFilterModel>({
        status: [UserRequestStatus.pending],
    } as IUserRequestsFilterModel);
    const [fetchingData, setFetchingData] = useState(false);
    const page = useRef(0);

    const [reproveModalOpen, setReproveModalOpen] = useState(false);
    const [userToReprove, setUserToReprove] = useState('');

    const [userToApprove, setUserToApprove] = useState({} as IApproveUser);
    const [approveModelOpen, setApproveModelOpen] = useState(false);

    const getUserByType = useCallback((user: number) => {
        return SULAHUB_USERS_BY_USER_TYPE[user]?.label;
    }, []);

    const getRequestedStatus = useCallback(
        (id: string, status: number) => {
            switch (status) {
                case 1:
                    return (
                        <ClbTag id={id} type="negative">
                            {t('Reprovado')}
                        </ClbTag>
                    );
                case 2:
                    return (
                        <ClbTag id={id} type="positive">
                            {t('Aprovado')}
                        </ClbTag>
                    );
                default:
                    return (
                        <ClbTag id={id} type="warning">
                            {t('Pendente')}
                        </ClbTag>
                    );
            }
        },
        [t],
    );

    const mapUsersToDataTable = useCallback(
        (data: IUsersRequestsResponse[]) => {
            return data.map(user => {
                return {
                    id: user.id,
                    requester: user.requester,
                    email: user.email,
                    carrrierId: user.carrrierId,
                    carrierName: user.carrierName ?? 'N/A',
                    userType: getUserByType(user.userType),
                    requestStatus: getRequestedStatus(user.id, user.status),
                    rowButtons: (
                        <RequestsTableRowButtons
                            rowId={user.id}
                            userTypeLabel={
                                SULAHUB_USERS_BY_USER_TYPE.find(
                                    type => type.value === user.userType,
                                )?.label
                            }
                            approveUser={(
                                userId: string,
                                userType?: number,
                                userTypeLabel?: string,
                            ) => {
                                setApproveModelOpen(true);
                                setUserToApprove({
                                    userId,
                                    userType,
                                    userTypeLabel,
                                });
                            }}
                            reproveUser={(userId: string) => {
                                setReproveModalOpen(true);
                                setUserToReprove(userId);
                            }}
                            userEmail={user.email}
                        />
                    ),
                } as IUserRequestsDataTable;
            });
        },
        [
            setReproveModalOpen,
            setUserToApprove,
            setUserToReprove,
            getUserByType,
            getRequestedStatus,
        ],
    );

    const dataTablePaginated = useCallback(
        (
            userData: IPagination<IUsersRequestsResponse>,
            newDataTable?: IUserRequestsDataTable[],
        ) => {
            return {
                data: newDataTable ?? mapUsersToDataTable(userData.data),
                currentPage: userData.currentPage,
                totalItems: userData.totalItems,
                totalPages: userData.totalPages,
            } as IPagination<IUserRequestsDataTable>;
        },
        [mapUsersToDataTable],
    );

    const fetchData = useCallback(
        async (backFirstPage?: boolean) => {
            const userData = await UsersManagementService.getRequestsUsersList(
                backFirstPage ? 1 : page.current,
                UsersConstants.PAGE_SIZE,
                filters.current,
            ).catch((error: Error) => {
                setToast('negative', 'Erro', error.message);
            });

            if (userData) {
                page.current = userData.currentPage;

                if (userData.currentPage === 1) {
                    setUsersDataTable(dataTablePaginated(userData));
                } else if (usersDataTable) {
                    const currentData: IUserRequestsDataTable[] = [
                        ...usersDataTable.data,
                    ];
                    const newData = mapUsersToDataTable(userData.data);
                    const newUsersDataTable = currentData.concat(newData);
                    setUsersDataTable(
                        dataTablePaginated(userData, newUsersDataTable),
                    );
                }
            }

            setFetchingData(false);
        },
        [dataTablePaginated, mapUsersToDataTable, setToast, usersDataTable],
    );

    useEffect(() => {
        if (page.current === 0) {
            page.current = 1;
            setFetchingData(true);
            fetchData();
        }

        if (
            searchTerm !== undefined &&
            searchTerm !== filters.current?.description
        ) {
            const delayDebounceFn = setTimeout(() => {
                filters.current = {
                    ...filters.current,
                    description: searchTerm,
                };
                fetchData(true);
            }, 300);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchTerm, fetchData, setFetchingData, page]);

    const loadMoreButtonHandler = useCallback(async (): Promise<void> => {
        page.current += 1;
        await fetchData();
    }, [fetchData, page]);

    const getTableHeaders = (): ITableHead[] => {
        const headers: ITableHead[] = [
            { id: 'request', label: t('Solicitante'), field: 'requester' },
            { id: 'email', label: t('Email'), field: 'email' },
            { id: 'userType', label: t('TipoUsuario'), field: 'userType' },
        ];

        if (!isThirdUser()) {
            headers.push(
                {
                    id: 'carrierName',
                    label: t('Transportadora'),
                    field: 'carrierName',
                },
                { id: 'aproval', label: t('Aprovacao'), contentIsButton: true },
            );
        }

        if (isThirdUser()) {
            headers.push({
                id: 'requestStatus',
                label: t('Status'),
                field: 'requestStatus',
            });
        }

        return headers;
    };

    const onReproveModalClose = () => setReproveModalOpen(false);

    const onApproveModalClose = () => {
        setUserToApprove({} as IApproveUser);
        setApproveModelOpen(false);
    };

    return (
        <>
            <Table
                content={usersDataTable}
                headers={getTableHeaders()}
                fetchingData={fetchingData}
                loadMoreButtonCallback={loadMoreButtonHandler}
                emptyTableDescription={t('NenhumUsuarioExibir')}
                hideToolbarTable
                dataTestid="request-user-table"
            />

            {!!reproveModalOpen && (
                <ReproveUserModal
                    fetchData={fetchData}
                    userToReprove={userToReprove}
                    reproveModalOpen={reproveModalOpen}
                    onReproveModalClose={onReproveModalClose}
                />
            )}

            {!!approveModelOpen && (
                <ApproveUserModal
                    userToApprove={{ ...userToApprove }}
                    approveModalOpen={approveModelOpen}
                    onApproveModalClose={onApproveModalClose}
                    fetchData={fetchData}
                />
            )}
        </>
    );
}

export default RequestsTable;
